import { z } from 'zod';

/**
 * What the web signup substance step offers: one entry per program, and an
 * optional single choice of sub-types. Structure only: the words (the label,
 * description, sub-type labels and notes) live in the signup catalog, keyed by
 * program key and sub-option value, so one structure serves every language.
 * The client renders these generically and never names a program (ADR-015);
 * it filters them by the sponsor's program keys.
 *
 * Client-parsed, so the schemas stay open: an unknown field from a newer
 * server is ignored rather than rejected (ADR-135).
 */
export const signupProgramSubOptionSchema = z.object({
  value: z.string().min(1),
  /**
   * The intake answer value this sub-type is saved as, under its option's
   * `subOptionAnswerName`. Absent: choosing it saves no sub-type answer.
   */
  answerValue: z.string().min(1).optional(),
});

export const signupProgramOptionSchema = z.object({
  /** A canonical program key, matched against the sponsor's `programKeys`. */
  programKey: z.string().min(1),
  /** A single choice shown under the option while it is selected. */
  subOptions: z.array(signupProgramSubOptionSchema).optional(),
  /**
   * The intake answer the chosen sub-type is saved under, so the server can
   * record it on the program it enrolls. Absent: no sub-type answer is saved.
   */
  subOptionAnswerName: z.string().min(1).optional(),
});

export const signupProgramOptionsSchema = z.object({
  options: z.array(signupProgramOptionSchema),
});

export type SignupProgramSubOption = z.infer<typeof signupProgramSubOptionSchema>;
export type SignupProgramOption = z.infer<typeof signupProgramOptionSchema>;
export type SignupProgramOptions = z.infer<typeof signupProgramOptionsSchema>;

/**
 * The options, in the legacy signup app's order. The web signup seam serves
 * them as is, real and local fake alike; an endpoint that later takes over
 * serves the same shape. Sub-option values are the sponsor-config keys
 * the legacy app sent for each sub-type; they key the catalog words. The
 * answer name and values are the ones the program catalog's sub-type
 * vocabulary maps to a tag, the same the app's own intake saves.
 */
export const SIGNUP_PROGRAM_OPTIONS: SignupProgramOptions = {
  options: [
    { programKey: 'alcohol' },
    {
      programKey: 'tobacco',
      subOptions: [
        { value: 'cigarettes', answerValue: 'cigarettes' },
        { value: 'eCigarettes', answerValue: 'vaping' },
        { value: 'chewingTobacco', answerValue: 'smokeless' },
      ],
      subOptionAnswerName: 'tobacco_subtype',
    },
    { programKey: 'cannabis' },
    { programKey: 'opioid' },
    { programKey: 'stimulants' },
  ],
};
